/* eslint-disable react/prop-types */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArtsLimitUpsellFlow } from '../../pages/ChatSocketPage/components/ArtsLimitUpsellFlow'
import { SubscribedArtsLimitUpsellFlow } from '../../pages/ChatSocketPage/components/SubscribedArtsLimitUpsellFlow'
import {
  openUpsellCheckoutSuccess,
  openUpsellFlow,
  resolveUpsellCheckoutSuccess,
  UPSELL_FLOWS,
  UpsellCheckoutProvider,
} from '../../pages/ChatSocketPage/components/UpsellFlows'
import {
  registerCheckoutRefreshRunner,
  savePendingCheckoutRefresh,
} from '../../pages/ChatSocketPage/components/UpsellFlows/upsellCheckoutRefresh'
import { EventMapService } from '../../services'
import { NavigationValidationService } from '../../services/navigationValidationService'
import { useClient } from '../../hooks/useClient'
import { usePaywall } from '../PaywallContext/usePaywall'
import { useCampaignSidebar } from '../CampaignSidebarContext/useCampaignSidebar'
import { useArtsLimitUpsellEligibility } from './useArtsLimitUpsellEligibility'

export const ArtsLimitUpsellContext = createContext(null)

export const ArtsLimitUpsellProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { clientData } = useClient()
  const { openPaywall } = usePaywall()
  const { refreshArtLimits, requestCreateCampaign } = useCampaignSidebar()

  const {
    isFlowBEnabled,
    isFlowCEnabled,
    refreshEligibility,
  } = useArtsLimitUpsellEligibility()

  const artsLimitUpsellFlowRef = useRef(null)
  const subscribedArtsLimitUpsellFlowRef = useRef(null)
  const returnToRef = useRef(null)
  const checkoutReturnHandledRef = useRef(false)

  const resolveFlowRef = useCallback(
    ({ isUserWithoutSubscription: withoutSub, canDownloadCampaigns: canDownload }) =>
      !withoutSub || canDownload
        ? subscribedArtsLimitUpsellFlowRef
        : artsLimitUpsellFlowRef,
    [],
  )

  const tryNavigateToReturnRoute = useCallback(async () => {
    const returnTo = returnToRef.current

    if (!returnTo) {
      return
    }

    try {
      const result = await NavigationValidationService.validateNavigation(returnTo, {
        clientData,
      })

      if (result.canNavigate) {
        returnToRef.current = null

        if (returnTo.toLowerCase() === '/chatsocket') {
          await requestCreateCampaign()
          return
        }

        navigate(returnTo, {
          state: result.navigationState || undefined,
        })
      }
    } catch (error) {
      console.error('Error validating navigation after arts limit upsell:', error)
    }
  }, [clientData, navigate, requestCreateCampaign])

  const handleFlowDismiss = useCallback(async () => {
    await refreshArtLimits()
    await tryNavigateToReturnRoute()
  }, [refreshArtLimits, tryNavigateToReturnRoute])

  const openArtsLimitUpsell = useCallback(
    async ({ returnTo = null, source = 'sidebar_navigation', reason = null } = {}) => {
      const eligibility = await refreshEligibility()

      returnToRef.current = returnTo

      if (source === 'sidebar_navigation') {
        EventMapService.postEventMap({
          event: 'insufficient_balance_offer_view',
          eventData: {
            source: 'sidebar_navigation',
            context: reason,
          },
        }).catch((error) => {
          console.error('Error tracking insufficient_balance_offer_view:', error)
        })
      }

      const flowRef = resolveFlowRef(eligibility)

      if (openUpsellFlow(flowRef, true)) {
        return true
      }

      openPaywall({
        reason: reason ?? 'art_generation_limit_reached',
        returnTo,
      })

      return false
    },
    [openPaywall, refreshEligibility, resolveFlowRef],
  )

  useEffect(() => {
    registerCheckoutRefreshRunner(async (payload) => {
      await refreshArtLimits()

      if (payload?.showToastsOnly) {
        window.dispatchEvent(
          new CustomEvent('user-plan-count-updated', {
            detail: { retry: false },
          }),
        )
      }
    })
  }, [refreshArtLimits])

  useEffect(() => {
    const handleCheckoutReturn = async () => {
      const url = new URL(window.location.href)
      const sessionId = url.searchParams.get('session_id')
      const hasAddonCheckoutSuccess =
        url.searchParams.get('addonCheckoutSuccess') === 'true' && !!sessionId
      const hasDownloadCheckoutSuccess =
        url.searchParams.get('downloadCheckoutSuccess') === 'true' && !!sessionId
      const hasSubscriptionCheckoutSuccess =
        !!sessionId &&
        (url.searchParams.get('subscriptionCheckoutSuccess') === 'true' ||
          (!url.searchParams.get('addonCheckoutSuccess') &&
            !url.searchParams.get('downloadCheckoutSuccess')))

      if (
        !hasAddonCheckoutSuccess &&
        !hasDownloadCheckoutSuccess &&
        !hasSubscriptionCheckoutSuccess
      ) {
        return
      }

      const upsellSuccess = resolveUpsellCheckoutSuccess(url.search)

      if (
        !upsellSuccess ||
        (upsellSuccess.flow !== UPSELL_FLOWS.ARTS_LIMIT &&
          upsellSuccess.flow !== UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT)
      ) {
        return
      }

      const checkoutKey = `${upsellSuccess.flow}:${upsellSuccess.step}:${sessionId}`

      if (checkoutReturnHandledRef.current === checkoutKey) {
        return
      }

      checkoutReturnHandledRef.current = checkoutKey

      const openedUpsellSuccess = openUpsellCheckoutSuccess(upsellSuccess, {
        artsLimitRef: artsLimitUpsellFlowRef,
        subscribedArtsLimitRef: subscribedArtsLimitUpsellFlowRef,
      })

      if (openedUpsellSuccess) {
        await refreshArtLimits()
        savePendingCheckoutRefresh({ showToastsOnly: true })
      }

      url.searchParams.delete('addonCheckoutSuccess')
      url.searchParams.delete('downloadCheckoutSuccess')
      url.searchParams.delete('subscriptionCheckoutSuccess')
      url.searchParams.delete('upsellFlow')
      url.searchParams.delete('upsellStep')
      url.searchParams.delete('campaignArtGenerationId')
      url.searchParams.delete('session_id')

      window.history.replaceState(
        {},
        '',
        `${url.pathname}${url.search}${url.hash}`,
      )
    }

    handleCheckoutReturn()
  }, [location.search, refreshArtLimits])

  const value = useMemo(
    () => ({
      openArtsLimitUpsell,
    }),
    [openArtsLimitUpsell],
  )

  return (
    <ArtsLimitUpsellContext.Provider value={value}>
      <UpsellCheckoutProvider>
        {children}

        <ArtsLimitUpsellFlow
          ref={artsLimitUpsellFlowRef}
          enabled={isFlowBEnabled}
          onDismiss={handleFlowDismiss}
        />

        <SubscribedArtsLimitUpsellFlow
          ref={subscribedArtsLimitUpsellFlowRef}
          enabled={isFlowCEnabled}
          onDismiss={handleFlowDismiss}
        />
      </UpsellCheckoutProvider>
    </ArtsLimitUpsellContext.Provider>
  )
}
